import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { Language, LinkedIn, X, Instagram, Facebook } from "@mui/icons-material";
import { FieldWrapper, ChoiceToggle, StepActions, PageHeader, SectionHeader, Callout } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useLazyGetLoanCreditUseQuery,
  useLazyPostLoanUseQuery,
  usePostValidateNextStageMutation,
} from "@api/applications";
import useStatusConfig from "@hooks/useStatusConfig";
import {
  useLazyGetLoanSocialMediaQuery,
  usePatchSocialMediaDataMutation,
  usePostSocialMediaMutation,
} from "@api/socialMediaApi";
import { ApiError } from "@interfaces/response";
import { PATHS_TOFU_V2, STATUS_CATALOG } from "@helpers/constantes";
import { InformacionEmpresaFormValues } from "@interfaces/informacionEmpresa";
import { WEP_PAGE } from "@utils/expreciones";

/**
 * "Empresa" (`FX_Empresa` del diseño): corre después de validar el teléfono (status CSDEM) y junta
 * el destino de la línea (misma estructura que `UsoCredito`) y los canales digitales (misma
 * estructura que `PresenciaDigital`). Los datos de identificación (RFC, nombre comercial y grupo
 * empresarial) se capturan antes, en `RegistroDatosPersonales`.
 *
 * Servicios: uso del crédito y redes sociales se cargan y guardan igual que en `UsoCredito` y
 * `PresenciaDigital`. Al terminar avanza el status CSDEM y navega a la URL que manda `status_config`.
 */

/** Mismo test de `PresenciaDigital`: con redes sociales, al menos una debe venir capturada. */
function atLeastOneSocialTest(this: yup.TestContext) {
  const { has_social_media, linkedin, facebook, instagram, twitter } = this.parent;
  if (has_social_media) {
    return linkedin || facebook || instagram || twitter ? true : false;
  }
  return true;
}

const SI_NO_BOOLEAN_OPTIONS = [
  { value: "si", label: "Sí" },
  { value: "no", label: "No" },
];

/** Sí/No de canales digitales: sin respuesta (`null`) no se marca ninguna opción. */
const toSiNoValue = (value: boolean | null) => (value === null ? "" : value ? "si" : "no");

/** Los textareas del kit parten de `lendia_input` (alto fijo): se libera el alto. */
const MULTILINE_SX = {
  "& .MuiOutlinedInput-root": { height: "auto" },
  "& .MuiOutlinedInput-input": { height: "auto", padding: "10px 14px" },
};

/** Descripción bajo el título de cada tarjeta (`.card-sub` del diseño). */
const CardSubtitle = ({ children }: { children: string }) => (
  <Typography variant="body2" color="text.secondary" sx={{ mt: "-6px", mb: 1, lineHeight: 1.5 }}>
    {children}
  </Typography>
);

export const InformacionEmpresa = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Status de esta pantalla: CSDEM (`datos_empresa_pm` y `datos_empresa_pfae` comparten el código).
  const { nextStatus, previousStatus } = useStatusConfig(STATUS_CATALOG.datos_empresa_pm.code);
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  // Uso del crédito (`UsoCredito`)
  const [getLoanCreditUse] = useLazyGetLoanCreditUseQuery();
  const [triggerSaveUseSelected] = useLazyPostLoanUseQuery();
  // Presencia digital (`PresenciaDigital`)
  const [getSocialMedia] = useLazyGetLoanSocialMediaQuery();
  const [postSocialMedia] = usePostSocialMediaMutation();
  const [patchSocialMedia] = usePatchSocialMediaDataMutation();

  /** Id del registro de redes sociales: si existe se hace patch, si no, post. */
  const [digitalPresenceId, setDigitalPresenceId] = useState("");
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);

  const formik = useFormik<InformacionEmpresaFormValues>({
    initialValues: {
      reason: "",
      activity: "",
      has_web_page: null,
      web_page: "",
      has_social_media: null,
      linkedin: "",
      facebook: "",
      instagram: "",
      twitter: "",
    },
    validationSchema: yup.object().shape({
      // Uso del crédito (`UsoCredito`)
      reason: yup.string().required("Campo requerido"),
      // Presencia digital (`PresenciaDigital`)
      has_web_page: yup.boolean().required("Campo requerido"),
      web_page: yup.string().when("has_web_page", {
        is: true,
        then: (schema) => schema.required("Campo requerido").matches(WEP_PAGE, "URL no válida"),
      }),
      has_social_media: yup.boolean().required("Campo requerido"),
      linkedin: yup
        .string()
        .matches(WEP_PAGE, "URL no válida")
        .test("at_least_one", "Debes completar al menos una red social con una URL válida", atLeastOneSocialTest),
      facebook: yup
        .string()
        .matches(WEP_PAGE, "URL no válida")
        .test("at_least_one", "Debes completar al menos una red social con una URL válida", atLeastOneSocialTest),
      instagram: yup
        .string()
        .matches(WEP_PAGE, "URL no válida")
        .test("at_least_one", "Debes completar al menos una red social con una URL válida", atLeastOneSocialTest),
      twitter: yup
        .string()
        .matches(WEP_PAGE, "URL no válida")
        .test("at_least_one", "Debes completar al menos una red social con una URL válida", atLeastOneSocialTest),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        // Uso del crédito: mismo payload que `UsoCredito`.
        await triggerSaveUseSelected({
          loanId: application.id,
          body: {
            reason: values.reason,
            // Ya no se pide en pantalla (el diseño la quitó): se reenvía la que estuviera guardada.
            activity: values.activity,
            business_sector_id: null,
            resource_uses_identifiers: [],
          },
        }).unwrap();

        // Redes sociales: mismo payload y post/patch que `PresenciaDigital`.
        const socialMediaData = {
          has_web_page: values.has_web_page ?? false,
          web_page: values.web_page || "",
          has_social_media: values.has_social_media ?? false,
          linkedin: values.linkedin || "",
          facebook: values.facebook || "",
          instagram: values.instagram || "",
          x_twitter: values.twitter || "",
        };
        if (digitalPresenceId) {
          await patchSocialMedia({
            client_details_id: clientDetails.id,
            social_media_id: digitalPresenceId,
            data: socialMediaData,
          }).unwrap();
        } else {
          await postSocialMedia({ client_details_id: clientDetails.id, data: socialMediaData }).unwrap();
        }

        // `onQueryStarted` de `postValidateNextStage` ya actualiza `loan_status`; no se despacha a mano.
        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        navigate(nextStatus.url);
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "InformacionEmpresa-submit", values } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const { values, setFieldValue, validateForm } = formik;
  const isFieldDisabled = formik.isSubmitting || isLoadingInitial;

  /**
   * Carga inicial con respaldo, igual que `UsoCredito` y `PresenciaDigital`: cada consulta es
   * independiente y si falla solo se reporta a Sentry; la pantalla se queda con los campos vacíos.
   */
  const getInitialData = useCallback(async () => {
    setIsLoadingInitial(true);
    const [creditUseResult, socialMediaResult] = await Promise.allSettled([
      getLoanCreditUse(application.id).unwrap(),
      getSocialMedia(clientDetails.id).unwrap(),
    ]);

    if (creditUseResult.status === "fulfilled" && creditUseResult.value) {
      await setFieldValue("reason", creditUseResult.value.reason || "", false);
      await setFieldValue("activity", creditUseResult.value.activity || "", false);
    } else if (creditUseResult.status === "rejected") {
      Sentry.captureException(creditUseResult.reason, { extra: { component: "InformacionEmpresa-credit_use" } });
    }

    if (socialMediaResult.status === "fulfilled" && socialMediaResult.value?.id) {
      const socialMedia = socialMediaResult.value;
      setDigitalPresenceId(socialMedia.id);
      await setFieldValue("has_web_page", socialMedia.has_web_page || false, false);
      await setFieldValue("web_page", socialMedia.web_page || "", false);
      await setFieldValue("has_social_media", socialMedia.has_social_media || false, false);
      await setFieldValue("linkedin", socialMedia.linkedin || "", false);
      await setFieldValue("facebook", socialMedia.facebook || "", false);
      await setFieldValue("instagram", socialMedia.instagram || "", false);
      await setFieldValue("twitter", socialMedia.x_twitter || "", false);
    } else if (socialMediaResult.status === "rejected") {
      Sentry.captureException(socialMediaResult.reason, { extra: { component: "InformacionEmpresa-social_media" } });
    }

    setIsLoadingInitial(false);
    // Igual que los originales: revalidar después de escribir los valores cargados.
    setTimeout(() => validateForm(), 200);
  }, [application.id, clientDetails.id, getLoanCreditUse, getSocialMedia, setFieldValue, validateForm]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const redesValues = [values.linkedin, values.facebook, values.instagram, values.twitter];
  const hasAtLeastOneRed = redesValues.some((v) => v.trim());
  const hasRedesError = Boolean(
    values.has_social_media &&
    (formik.errors.linkedin || formik.errors.facebook || formik.errors.instagram || formik.errors.twitter),
  );

  return (
    <Box>
      <PageHeader
        eyebrow="Empresa"
        title="¿Para qué usarás la línea?"
        subtitle="Destino del factoraje y canales digitales de la empresa; los datos de identificación ya los tenemos."
      />

      {/* Información del financiamiento (estructura de `UsoCredito`) */}
      <Paper variant="lendia_paper" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader label="Información del financiamiento" first />
        <CardSubtitle>Cuéntanos para qué usarás la línea de factoraje.</CardSubtitle>
        <FieldWrapper label="Destino de la línea" full error={(formik.touched.reason && formik.errors.reason) || ""}>
          <TextField
            autoComplete="off"
            size="lendia_input"
            fullWidth
            multiline
            minRows={3}
            name="reason"
            value={values.reason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Describe en qué usarás los anticipos. Ej. capital de trabajo para cubrir el ciclo de cobro de mis clientes…"
            inputProps={{ maxLength: 150, "data-testid": "informacion-empresa-destino-input" }}
            disabled={isFieldDisabled}
            sx={MULTILINE_SX}
          />
        </FieldWrapper>
      </Paper>

      {/* Canales digitales (estructura de `PresenciaDigital`) */}
      <Paper variant="lendia_paper" sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
        <SectionHeader label="Canales digitales" first />
        <CardSubtitle>Agrega los canales digitales de la empresa, si aplica.</CardSubtitle>
        <FieldWrapper label="¿La empresa tiene página web?">
          <ChoiceToggle
            value={toSiNoValue(values.has_web_page)}
            onChange={(v) => {
              const value = v === "si";
              setFieldValue("has_web_page", value);
              if (!value) setFieldValue("web_page", "");
              setTimeout(() => validateForm(), 200);
            }}
            options={SI_NO_BOOLEAN_OPTIONS}
            size="small"
            disabled={isFieldDisabled}
          />
        </FieldWrapper>

        {values.has_web_page && (
          <FieldWrapper label="URL del sitio" error={(formik.touched.web_page && formik.errors.web_page) || ""}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="web_page"
              value={values.web_page}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="empresa.com"
              inputProps={{ maxLength: 255, "data-testid": "informacion-empresa-web-input" }}
              disabled={isFieldDisabled}
              InputProps={{ startAdornment: <Language fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
            />
          </FieldWrapper>
        )}

        <SectionHeader label="Redes sociales" />
        <FieldWrapper label="¿La empresa tiene redes sociales?">
          <ChoiceToggle
            value={toSiNoValue(values.has_social_media)}
            onChange={(v) => {
              const value = v === "si";
              setFieldValue("has_social_media", value);
              if (!value) {
                setFieldValue("linkedin", "");
                setFieldValue("facebook", "");
                setFieldValue("instagram", "");
                setFieldValue("twitter", "");
              }
              setTimeout(() => validateForm(), 200);
            }}
            options={SI_NO_BOOLEAN_OPTIONS}
            size="small"
            disabled={isFieldDisabled}
          />
        </FieldWrapper>

        {values.has_social_media && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FieldWrapper label="LinkedIn" optionalSuffix="(opcional)">
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="linkedin"
                value={values.linkedin}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="linkedin.com/company/empresa"
                inputProps={{ maxLength: 255, "data-testid": "informacion-empresa-linkedin-input" }}
                disabled={isFieldDisabled}
                InputProps={{ startAdornment: <LinkedIn fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
              />
            </FieldWrapper>
            <FieldWrapper label="X / Twitter" optionalSuffix="(opcional)">
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="twitter"
                value={values.twitter}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="@empresa"
                inputProps={{ maxLength: 255, "data-testid": "informacion-empresa-twitter-input" }}
                disabled={isFieldDisabled}
                InputProps={{ startAdornment: <X fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
              />
            </FieldWrapper>
            <FieldWrapper label="Instagram" optionalSuffix="(opcional)">
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="instagram"
                value={values.instagram}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="@empresa"
                inputProps={{ maxLength: 255, "data-testid": "informacion-empresa-instagram-input" }}
                disabled={isFieldDisabled}
                InputProps={{ startAdornment: <Instagram fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
              />
            </FieldWrapper>
            <FieldWrapper label="Facebook" optionalSuffix="(opcional)">
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="facebook"
                value={values.facebook}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="facebook.com/empresa"
                inputProps={{ maxLength: 255, "data-testid": "informacion-empresa-facebook-input" }}
                disabled={isFieldDisabled}
                InputProps={{ startAdornment: <Facebook fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
              />
            </FieldWrapper>

            {hasRedesError && (
              <Callout tone="warn">
                {!hasAtLeastOneRed
                  ? "Debes completar al menos una red social con una URL válida"
                  : "Alguna de las URLs no es válida"}
              </Callout>
            )}
          </Box>
        )}
      </Paper>

      <StepActions
        // Regreso secuencial según `status_config`; la ruta fija queda de respaldo.
        onBack={() => navigate(previousStatus?.url || PATHS_TOFU_V2.validacionTelefono.path)}
        onNext={formik.submitForm}
        nextDisabled={!formik.isValid || isFieldDisabled}
        backTestId="informacion-empresa-back-button"
        nextTestId="informacion-empresa-continue-button"
      />
    </Box>
  );
};
